import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Calendar, User, Building, CheckCircle, Loader2 } from 'lucide-react';
import { useProject } from '../hooks/usePortfolioData';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const ProjectDetail = () => {
  const { slug } = useParams();
  const { project, loading } = useProject(slug);

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-gray-400" size={48} />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="pt-20 py-16">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Project Not Found</h1>
          <Button asChild>
            <Link to="/projects">Back to Projects</Link>
          </Button>
        </div>
      </div>
    );
  }

  const hasImages = project.images && (project.images.main || project.images.render || (project.images.gallery && project.images.gallery.length > 0));

  return (
    <div className="pt-20">
      {/* Back Navigation */}
      <div className="py-6 border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6">
          <Button variant="ghost" asChild>
            <Link to="/projects" className="flex items-center space-x-2">
              <ArrowLeft size={16} />
              <span>Back to Projects</span>
            </Link>
          </Button>
        </div>
      </div>

      {/* Project Header */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <Badge variant="secondary">{project.status}</Badge>
                <Badge>{project.year}</Badge>
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-6">{project.name}</h1>

              <div className="grid grid-cols-2 gap-6 mb-6">
                <div>
                  <div className="flex items-center space-x-2 text-gray-600 mb-2">
                    <MapPin size={16} />
                    <span className="text-sm font-medium">Location</span>
                  </div>
                  <p className="text-gray-900">{project.location}</p>
                </div>
                <div>
                  <div className="flex items-center space-x-2 text-gray-600 mb-2">
                    <Building size={16} />
                    <span className="text-sm font-medium">Area</span>
                  </div>
                  <p className="text-gray-900">{project.area}</p>
                </div>
                <div>
                  <div className="flex items-center space-x-2 text-gray-600 mb-2">
                    <User size={16} />
                    <span className="text-sm font-medium">Role</span>
                  </div>
                  <p className="text-gray-900">{project.role}</p>
                </div>
                <div>
                  <div className="flex items-center space-x-2 text-gray-600 mb-2">
                    <Calendar size={16} />
                    <span className="text-sm font-medium">Type</span>
                  </div>
                  <p className="text-gray-900">{project.type}</p>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Client</h3>
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                    <Building className="text-gray-400" size={20} />
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">{project.client}</div>
                    <div className="text-sm text-gray-500">{project.company}</div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              {project.images?.main && !project.renderOnly && (
                <div className="relative mb-6">
                  <img
                    src={project.images.main}
                    alt={`${project.name} - Main Image`}
                    className="w-full h-80 object-cover rounded-lg shadow-lg"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <div className="absolute bottom-4 left-4 bg-black/70 text-white px-3 py-1 rounded text-sm">
                    Project Main Image
                  </div>
                </div>
              )}
              {!project.images?.main && !project.renderOnly && (
                <div className="w-full h-80 bg-gradient-to-br from-gray-200 to-gray-300 rounded-lg flex items-center justify-center">
                  <Building className="text-gray-400" size={64} />
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Project Description */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Project Overview</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                {project.description}
              </p>

              {project.keyAchievements && project.keyAchievements.length > 0 && (
                <>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Key Achievements</h3>
                  <div className="space-y-3">
                    {project.keyAchievements.map((achievement, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <CheckCircle className="text-green-600 mt-1 flex-shrink-0" size={16} />
                        <span className="text-gray-700">{achievement}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div>
              {project.technologies && project.technologies.length > 0 && (
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-lg mb-4">Technologies & Tools</h3>
                    <div className="space-y-2">
                      {project.technologies.map((tech, index) => (
                        <Badge key={index} variant="outline" className="mr-2 mb-2">
                          {tech}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Project Images */}
      {hasImages && (
        <section className="py-12">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Project Gallery</h2>

            {/* Main Images - Render and Final */}
            <div className={`grid gap-8 mb-12 ${project.images?.render && project.images?.main && !project.renderOnly ? 'md:grid-cols-2' : 'md:grid-cols-1'}`}>
              {project.images?.render && (
                <div className="relative">
                  <img
                    src={project.images.render}
                    alt={`${project.name} - Concept Render`}
                    className="w-full h-80 object-cover rounded-lg shadow-lg"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <div className="absolute bottom-4 left-4 bg-black/70 text-white px-3 py-2 rounded">
                    <div className="font-medium">Concept Render</div>
                    <div className="text-sm opacity-90">Design visualization</div>
                  </div>
                </div>
              )}
              {project.images?.main && !project.renderOnly && (
                <div className="relative">
                  <img
                    src={project.images.main}
                    alt={`${project.name} - Final Result`}
                    className="w-full h-80 object-cover rounded-lg shadow-lg"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <div className="absolute bottom-4 left-4 bg-black/70 text-white px-3 py-2 rounded">
                    <div className="font-medium">Final Result</div>
                    <div className="text-sm opacity-90">Completed project</div>
                  </div>
                </div>
              )}
            </div>

            {/* Additional Gallery Images */}
            {project.images?.gallery && project.images.gallery.length > 0 && (
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-6">Additional Project Photos</h3>
                <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {project.images.gallery.map((image, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={image}
                        alt={`${project.name} - Photo ${index + 1}`}
                        className="w-full h-48 object-cover rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all rounded-lg"></div>
                      <div className="absolute bottom-2 right-2 bg-black/60 text-white px-2 py-1 rounded text-xs">
                        {index + 1}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Navigation */}
      <section className="py-12 border-t border-gray-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex justify-between">
            <Button variant="outline" asChild>
              <Link to="/projects">View All Projects</Link>
            </Button>
            <Button asChild>
              <Link to="/contact">Discuss Similar Project</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProjectDetail;
